import { useTranslation } from 'react-i18next';
import classNames from 'classnames';

import Tooltip from '@/components/atoms/Tooltip';
import { formatDateShortLocalized } from '@/utils/date';

import { transferAmount } from '../../helpers/transferFacts';
import type { BlockTransfer } from '../../types';

import styles from './GroupLabel.module.scss';

interface OriginToggleProps {
    count: number;
    /** The row's earlier rows are on screen. */
    pressed: boolean;
    /** The transfers that brought fish into the row from other rows. */
    origins: BlockTransfer[];
    /** Earlier rows kept on screen because other rows descend from them too. */
    sharedCount: number;
    /** The row's lineage is lit and the others dimmed; a press clears it. */
    highlighted: boolean;
    onToggle: () => void;
}

/**
 * "Origin (n)": hides the earlier rows only this row came from, or, when every one of them is
 * shared, lights its lineage. Its tooltip lists each source with the fish it sent.
 */
export default function OriginToggle(props: OriginToggleProps) {
    const { count, pressed, origins, sharedCount, highlighted, onToggle } = props;
    const { t, i18n } = useTranslation('fishGroups');
    const locale = i18n.language;
    const label = t('board.row.origin', { count });

    const lines = origins.map((origin) =>
        t('board.row.originSource', {
            name: origin.sourceName,
            date: formatDateShortLocalized(origin.date, locale),
            amount: transferAmount(origin, locale, t),
        })
    );
    if (sharedCount > 0) lines.push(t('board.lineage.shared', { count: sharedCount }));
    if (highlighted) lines.push(t('board.lineage.highlightHint'));

    return (
        <Tooltip
            content={lines.join('\n')}
            heading={origins.length > 0 ? t('board.row.originHeading') : undefined}
            disabled={lines.length === 0}
            layout="fit"
            targetClassName={styles.originTarget}>
            <button
                type="button"
                className={classNames(styles.originToggle, {
                    [styles.originToggleLit]: highlighted,
                })}
                aria-pressed={pressed}
                aria-label={highlighted ? t('board.lineage.litName', { origin: label }) : label}
                onClick={onToggle}>
                {label}
            </button>
        </Tooltip>
    );
}
